/**
 * Service to fetch projection geometry vectors from the PHP backend.
 *
 * This keeps the calculation logic server-side and not exposed to the client.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api.php';

/**
 * Fetch computed projection vectors from the server.
 *
 * @param {Object} params - The geometry parameters
 * @param {string} params.geometryType - Type of geometry ('laminography', 'laminography_source_perp', 'tomography')
 * @param {number} params.numProjections - Number of projection angles
 * @param {number} params.startAngle - Start angle in degrees
 * @param {number} params.endAngle - End angle in degrees
 * @param {number} params.tilt - Tilt angle in degrees
 * @param {number} params.sod - Source-to-object distance
 * @param {number} params.odd - Object-to-detector distance
 * @param {number} params.pitch - Detector pixel pitch
 * @param {number} [params.offsetX=0] - Detector X offset
 * @param {number} [params.offsetY=0] - Detector Y offset
 * @param {boolean} [params.fovCenterAsOrigin=false] - Use FOV center as origin
 * @returns {Promise<{vectors: number[][], metadata: Object}>} The computed vectors and metadata
 */
export async function fetchVectors(params) {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      geometryType: params.geometryType,
      numProjections: params.numScans,
      startAngle: params.startAngle,
      endAngle: params.endAngle,
      tilt: params.tilt,
      sod: params.sod,
      odd: params.odd,
      pitch: params.pitch,
      offsetX: params.offsetX || 0,
      offsetY: params.offsetY || 0,
      fovCenterAsOrigin: params.fovCenterAsOrigin || false,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Unknown error occurred');
  }

  return {
    vectors: data.vectors,
    metadata: data.metadata,
  };
}

/**
 * Helper to generate angles locally (for visualization only, not for actual computation).
 * The actual vector computation happens server-side.
 *
 * @param {number} startAngle - Start angle in degrees
 * @param {number} endAngle - End angle in degrees
 * @param {number} numProjections - Number of projections
 * @returns {number[]} Array of angles in radians
 */
export function generateAnglesForVisualization(startAngle, endAngle, numProjections) {
  const angles = [];
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  if (numProjections === 1) {
    return [startRad];
  }

  const step = (endRad - startRad) / (numProjections - 1);

  for (let i = 0; i < numProjections; i++) {
    angles.push(startRad + i * step);
  }

  return angles;
}

export default fetchVectors;
